import { Button, Card, Chip, Paper, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'
import moment from 'moment'
import React from 'react'
import { Link as ReactRouterLink, useParams, useRouteMatch } from 'react-router-dom'
import CommonPageWrapper from '../../../common/components/CommonPageWrapper'
import Loader from '../../../common/loader/Loader'
import { removeTypename } from '../../../common/utils'
import { Query } from '../../../react-apollo/Components'
import { i18n } from '../../index'
import { fetchExpressCheckoutPeriodPage } from '../expressCheckoutQueries'
import GoBackButton from '../GoBackButton'
import { Category, OpeningHoursPeriod, RestaurantMenu, RestaurantOpeningHours } from '../types'
import { weekdayToTranslateKey } from '../util'

export function CategoryPeriodListPage() {
  const match = useRouteMatch()
  const url = match.url
  const { stationId, categoryId } = useParams<{ stationId: string; categoryId: string }>()

  return (
    <CommonPageWrapper title={i18n.t('restaurantMenu.categoryDialog.openingHours')}>
      <GoBackButton stationId={stationId} to={`/pikakassa/${stationId}/aukioloajat`} />
      <Stack spacing={2}>
        <Query<
          { expressCheckoutMenu?: RestaurantMenu; expressCheckoutCategoryPeriods?: OpeningHoursPeriod[] },
          { stationId: string; categoryId: string }
        >
          query={fetchExpressCheckoutPeriodPage}
          variables={{ stationId: decodeURIComponent(stationId), categoryId }}
          fetchPolicy='network-only'
        >
          {({ data, loading }) => {
            const categories = data?.expressCheckoutMenu?.categories ?? []
            const category = categories.find((c: Category) => c.id === categoryId)
            // TODO: Maybe do this filtering in the backend instead
            const categoryPeriods =
              data?.expressCheckoutCategoryPeriods?.filter((p: OpeningHoursPeriod) => p.categoryId === categoryId) ?? []
            return (
              <Loader loading={loading}>
                <>
                  <Typography variant='h6' gutterBottom>
                    {`${i18n.t('restaurantMenu.categoryDialog.openingHours')} - ${category?.name}`}
                  </Typography>
                  <Stack spacing={2}>
                    {categoryPeriods.map((period) => (
                      <PeriodCard key={period.periodId} period={period} url={url} />
                    ))}
                    <Card>
                      <Stack direction='row' justifyContent='center' padding={2}>
                        <Button component={ReactRouterLink} variant='contained' to={`${url}/uusi`}>
                          {i18n.t('restaurantMenu.categoryPeriods.newCategoryPeriod')}
                        </Button>
                      </Stack>
                    </Card>
                  </Stack>
                </>
              </Loader>
            )
          }}
        </Query>
      </Stack>
    </CommonPageWrapper>
  )
}

function PeriodCard({ period, url }: { period: OpeningHoursPeriod; url: string }) {
  const isActive = moment().isBetween(moment(period.startDate).startOf('day'), moment(period.endDate).endOf('day'))
  return (
    <Paper>
      <Stack spacing={2}>
        <Stack direction='row' spacing={2} padding={2}>
          <Typography variant='h6'>{period.periodName}</Typography>
          <Typography variant='body1'>
            {moment(period.startDate).format('DD.MM.YYYY')} - {moment(period.endDate).format('DD.MM.YYYY')}
          </Typography>
          {isActive && <Chip size='small' color='primary' label={i18n.t('restaurantMenu.currentlyActive')} />}
        </Stack>
        <Table>
          <TableBody>
            {Object.keys(removeTypename(period.openingHours)).map((weekDay) => {
              const translateKey = weekdayToTranslateKey(weekDay as keyof RestaurantOpeningHours)
              const { opens, closes, schedules } = period.openingHours[weekDay as keyof RestaurantOpeningHours]
              const displayedHours =
                schedules && schedules.length > 0
                  ? schedules.map((s) => `${s.opens} - ${s.closes}`).join(', ')
                  : `${opens} - ${closes}`
              return (
                <TableRow key={weekDay}>
                  <TableCell>
                    <Typography>{i18n.t(translateKey)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{displayedHours}</Typography>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        <Stack direction='row' justifyContent='flex-end'>
          <Button component={ReactRouterLink} variant='outlined' to={`${url}/${period.periodId}/muokkaa`}>
            {i18n.t('buttons.edit')}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}
